import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { IStringMap } from 'app/interfaces/IStringMap';
import { getValue } from 'app/extensibility/utils/property-bag.util';
import { Constants } from 'app/shared/shared-constants';
import { akvToModel, akvToResource, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, azureStorageAuthTypes } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureStorageLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureStorageLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && ls.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication && !ls.enableAccountAKV) {
                        if (ls.accountKey instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                accountKey: 'secret',
                sasUri: 'secret',
                sasToken: 'secret',
                encryptedCredential: '*'
            }
        }
    }
})
export class AzureStorageLinkedServiceAdapter extends LinkedServiceAdapter<AzureStorageLinkedService> {
    protected modelConstructor(): new () => AzureStorageLinkedService {
        return AzureStorageLinkedService;
    }

    protected model(): IModel {
        return AzureStorageLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureStorageLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureStorageLinkedService;

        properties.type = Metamodel.modelFor(AzureStorageLinkedService).name;

        if (linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
            if (linkedService.enableSasUriAKV && linkedService.referedSasUriAKV instanceof AzureKeyVault) {
                properties.typeProperties = {
                    sasUri: akvToResource(linkedService.referedSasUriAKV, this.context)
                };
            } else if (isString(linkedService.sasUri)) {
                let sasUri = linkedService.sasUri as string;
                if (linkedService.sasToken && isString(linkedService.sasToken)) {
                    let sasToken = linkedService.sasToken as string;
                    if (sasUri.endsWith('?')) {
                        sasUri = sasUri.substr(0, sasUri.length - 1);
                    }
                    if (sasToken.startsWith('?')) {
                        sasToken = sasToken.substr(1);
                    }
                    properties.typeProperties = {
                        sasUri: sasUri + '?' + sasToken
                    };
                } else {
                    properties.typeProperties = {
                        sasUri,
                        sasToken: (linkedService.sasToken && (linkedService.sasToken instanceof AzureKeyVault)) ? akvToResource(linkedService.sasToken, this.context) : undefined
                    };
                }
            }
        } else {
            if (!linkedService.enableAccountAKV) {
                const propertiesBag: IStringMap<string | undefined> = {};
                propertiesBag['DefaultEndpointsProtocol'] = 'https';
                propertiesBag['AccountName'] = linkedService.accountName;
                if (linkedService.accountKey && !(linkedService.accountKey instanceof AzureKeyVault)) {
                    propertiesBag['AccountKey'] = linkedService.accountKey;
                }
                if (linkedService.endpointSuffix != null && linkedService.endpointSuffix !== '') {
                    propertiesBag['EndpointSuffix'] = linkedService.endpointSuffix;
                }
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        if (pair.value != null) {
                            propertiesBag[pair.key] = pair.value;
                        }
                    });
                }
                let connectionString = '';
                Object.keys(propertiesBag).forEach((key) => {
                    const value = getValue(propertiesBag, key);
                    if (value != null) {
                        connectionString += `${key}=${value};`;
                    }
                });
                properties.typeProperties = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    connectionString: linkedService.accountName === Constants.maskedCredential
                        && linkedService.accountKey === Constants.maskedCredential ?
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        ({
                            type: 'SecureString',
                            value: Constants.maskedCredential
                        } as Model.SecureString) : this._connectionStringToResource(connectionString),
                    accountKey: linkedService.accountKey instanceof AzureKeyVault ? akvToResource(linkedService.accountKey, this.context) : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAccountAKV, this.context)
                };
            }
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureStorageLinkedService): Promise<AzureStorageLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureStorageLinkedService;

        linkedService.accountName = undefined;
        linkedService.accountKey = undefined;
        linkedService.referedAccountAKV = undefined;
        linkedService.sasToken = undefined;
        linkedService.sasUri = undefined;
        linkedService.referedSasUriAKV = undefined;
        linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
        linkedService.additionalFields = [];
        if (properties.typeProperties.sasUri) {
            linkedService.authenticationMethod = azureStorageAuthTypes.SasUriAuthentication;
            if (isString(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = properties.typeProperties.sasUri;
                linkedService.sasToken = secretBaseToPasswordOrAKV(properties.typeProperties.sasToken as Model.SecretBase, true, this.context);
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = Constants.maskedCredential;
                linkedService.sasToken = secretBaseToPasswordOrAKV(properties.typeProperties.sasToken as Model.SecretBase, true, this.context);
            } else if ((properties.typeProperties.sasUri as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.enableSasUriAKV = true;
                linkedService.referedSasUriAKV = akvToModel(properties.typeProperties.sasUri as Model.AzureKeyVaultSecretReference, this.context);
            }
        } else if (isString(properties.typeProperties.connectionString)) {
            const keyValPairs = (properties.typeProperties.connectionString as string).split(';');
            keyValPairs.forEach((item) => {
                // assume key doesn't contain '='
                const sepIndex = item.indexOf('=');
                if (sepIndex >= 0) {
                    const key = item.substr(0, sepIndex);
                    const val = item.substr(sepIndex + 1);
                    if (key === 'AccountName') {
                        linkedService.accountName = val;
                    }
                    if (key === 'EndpointSuffix') {
                        linkedService.endpointSuffix = val;
                    }
                    if (key === 'DefaultEndpointsProtocol') {
                        if (val !== 'https') {
                            linkedService.additionalFields.push({ key, value: val });
                        }
                    } else if (key !== 'AccountKey' && key !== 'AccountName' && key !== 'EndpointSuffix') {
                        linkedService.additionalFields.push({ key, value: val });
                    }
                }
            });
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                linkedService.accountKey = secretBaseToPasswordOrAKV(properties.typeProperties.accountKey as Model.SecretBase, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.accountName = Constants.maskedCredential;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                linkedService.accountKey = secretBaseToPasswordOrAKV(properties.typeProperties.accountKey as Model.SecretBase, false, this.context);
            }
        } else if (properties.typeProperties.connectionString && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAccountAKV = akvToModel(properties.typeProperties.connectionString as Model.SecretBase, this.context);
            linkedService.enableAccountAKV = true;
        }

        return linkedService;
    }
}
